import React, { useRef, useState } from "react"
import moment from "moment"
import "moment/locale/bg"
import "moment/locale/tr"
import { Popover, Typography, Box } from "@mui/material"
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { YearCalendar } from "@mui/x-date-pickers"
import { bgBG, enUS, trTR } from "@mui/x-date-pickers/locales"
import { trans } from "@/_providers/Translation"
import { useAuthDataContext } from "@/_providers/Auth"
// import calendarImage from '@/assets/img/app/icons/calendar.svg'
// import arrowDownImage from '@/assets/img/app/icons/arrow-down.svg'

const getLocaleText = (locale) =>
  locale.components.MuiLocalizationProvider.defaultProps.localeText

// Map the browser language (primary subtag) to a moment locale + MUI localeText.
const LOCALES = {
  bg: { adapterLocale: "bg", localeText: getLocaleText(bgBG) },
  en: { adapterLocale: "en", localeText: getLocaleText(enUS) },
  tr: { adapterLocale: "tr", localeText: getLocaleText(trTR) },
}

const getBrowserLocale = () => {
  const language = (navigator.language || navigator.userLanguage || "bg")
    .slice(0, 2)
    .toLowerCase()

  return LOCALES[language] || LOCALES.bg
}

moment.locale("bg")

export default function DateRange({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  disabled,
  yearOnly = false,
  monthOnly = false,
  weekOnly = false,
}) {
  const { lang } = useAuthDataContext()

  const containerRef = useRef(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [pickerType, setPickerType] = useState(null) // 'start' or 'end'

  const valueFormat = yearOnly ? "YYYY" : monthOnly ? "YYYY-MM" : "YYYY-MM-DD"

  const formatDate = (dateString) => {
    if (!dateString) return ""

    if (yearOnly) return `${dateString} г.`

    if (monthOnly) return moment(dateString, "YYYY-MM").format("MMM YYYY")

    return moment(dateString).format("DD MMM,YY")
  }

  const handleStartDateChange = (newValue) => {
    const newStartDate = newValue ? newValue.format(valueFormat) : null

    // Selecting a start date auto-fills the end date 7 days later.
    if (weekOnly) {
      const newEndDate = newValue
        ? newValue.clone().add(7, "days").format(valueFormat)
        : null
      onStartDateChange?.(newStartDate)
      onEndDateChange?.(newEndDate)
      handleClose()
      return
    }

    // If new start date is after end date, adjust end date
    if (newStartDate && endDate && newStartDate > endDate) {
      onEndDateChange?.(newStartDate)
    }

    onStartDateChange?.(newStartDate)

    // Close current popover and auto-open end date picker
    setPickerType("end")
  }

  const handleEndDateChange = (newValue) => {
    const newEndDate = newValue ? newValue.format(valueFormat) : null

    // Selecting an end date auto-fills the start date 7 days earlier.
    if (weekOnly) {
      const newStartDate = newValue
        ? newValue.clone().subtract(7, "days").format(valueFormat)
        : null
      onEndDateChange?.(newEndDate)
      onStartDateChange?.(newStartDate)
      handleClose()
      return
    }

    onEndDateChange?.(newEndDate)
    handleClose()
  }

  const handleClose = () => {
    setAnchorEl(null)
    setPickerType(null)
  }

  const handleContainerClick = (e) => {
    // Don't open start picker if clicking on end date area
    if (e.target.closest(".date-range-end")) {
      return
    }

    if (disabled) return

    setAnchorEl(containerRef.current)
    setPickerType("start")
  }

  const handleEndClick = (e) => {
    e.stopPropagation()

    if (disabled) return

    setAnchorEl(containerRef.current)
    setPickerType("end")
  }

  const open = Boolean(anchorEl)

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={lang()}>
      <div
        className="date-range-picker"
        ref={containerRef}
        onClick={handleContainerClick}
        style={{
          pointerEvents: disabled ? "none" : "auto",
          opacity: disabled ? 0.4 : 1,
        }}
      >
        {/* <img src={calendarImage} alt="calendar" className="date-range-icon" /> */}

        <div className="date-range-display">
          {!startDate || !String(startDate).length ? (
            <span
              style={{
                paddingLeft: "5px",
              }}
            >
              {trans("random.selectPeriod")}
            </span>
          ) : (
            <>
              <span
                className="date-range-start"
                style={
                  yearOnly
                    ? { width: "60px" }
                    : monthOnly
                      ? { width: "90px" }
                      : {}
                }
              >
                {!startDate || !String(startDate).length
                  ? trans("random.select")
                  : formatDate(startDate)}
              </span>
              <span className="date-range-separator">- </span>
              <span
                className="date-range-end"
                onClick={handleEndClick}
                style={
                  yearOnly
                    ? { width: "60px" }
                    : monthOnly
                      ? { width: "90px" }
                      : {}
                }
              >
                {!endDate || !String(endDate).length
                  ? trans("random.select")
                  : formatDate(endDate)}
              </span>
            </>
          )}
        </div>

        {/* <img src={arrowDownImage} alt="dropdown" className="date-range-arrow" /> */}
      </div>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{
          zIndex: 999999,
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 2,
            },
          },
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ pt: 2, px: 3, color: "text.secondary", fontWeight: 600 }}
        >
          {pickerType === "start"
            ? trans("filters.startDate")
            : trans("filters.endDate")}
        </Typography>
        <Box>
          {yearOnly ? (
            <>
              {pickerType === "start" && (
                <YearCalendar
                  value={startDate ? moment(startDate, "YYYY") : null}
                  maxDate={endDate ? moment(endDate, "YYYY") : undefined}
                  onChange={handleStartDateChange}
                />
              )}
              {pickerType === "end" && (
                <YearCalendar
                  value={endDate ? moment(endDate, "YYYY") : null}
                  minDate={startDate ? moment(startDate, "YYYY") : undefined}
                  onChange={handleEndDateChange}
                />
              )}
            </>
          ) : monthOnly ? (
            <>
              {pickerType === "start" && (
                <DateCalendar
                  views={["year", "month"]}
                  openTo="month"
                  value={startDate ? moment(startDate, "YYYY-MM") : null}
                  maxDate={endDate ? moment(endDate, "YYYY-MM") : undefined}
                  onChange={(newValue, state) => {
                    if (state === "finish") {
                      handleStartDateChange(newValue)
                    } else {
                      // Keep the controlled value in sync on the year
                      // (partial) step so the final month selection retains
                      // the year the user navigated to.
                      onStartDateChange?.(
                        newValue ? newValue.format(valueFormat) : null,
                      )
                    }
                  }}
                />
              )}
              {pickerType === "end" && (
                <DateCalendar
                  views={["year", "month"]}
                  openTo="month"
                  value={endDate ? moment(endDate, "YYYY-MM") : null}
                  minDate={startDate ? moment(startDate, "YYYY-MM") : undefined}
                  onChange={(newValue, state) => {
                    if (state === "finish") {
                      handleEndDateChange(newValue)
                    } else {
                      // Keep the controlled value in sync on the year
                      // (partial) step so the final month selection retains
                      // the year the user navigated to.
                      onEndDateChange?.(
                        newValue ? newValue.format(valueFormat) : null,
                      )
                    }
                  }}
                />
              )}
            </>
          ) : weekOnly ? (
            <>
              {pickerType === "start" && (
                <DateCalendar
                  value={startDate ? moment(startDate) : null}
                  onChange={handleStartDateChange}
                />
              )}
              {pickerType === "end" && (
                <DateCalendar
                  value={endDate ? moment(endDate) : null}
                  onChange={handleEndDateChange}
                />
              )}
            </>
          ) : (
            <>
              {pickerType === "start" && (
                <DateCalendar
                  value={startDate ? moment(startDate) : null}
                  maxDate={endDate ? moment(endDate) : undefined}
                  onChange={handleStartDateChange}
                />
              )}
              {pickerType === "end" && (
                <DateCalendar
                  value={endDate ? moment(endDate) : null}
                  minDate={startDate ? moment(startDate) : undefined}
                  onChange={handleEndDateChange}
                />
              )}
            </>
          )}
        </Box>
      </Popover>
    </LocalizationProvider>
  )
}
